import { Component, Mixins, Prop } from 'vue-property-decorator'
import CatalogElementMixin from '../../mixins/CatalogElementMixin'
import { fetchService, needFetchService } from '../../fetchDatas/service'
import { KForm, KFormInput, KFormSelect, KFormEditor, KFormNumber, KFormSpinner, KFormCheckbox } from '@kwixeo/form-vue'
import { BFormRow, BCol, BFormGroup, BRow, BTabs, BTab, BInputGroup, BFormCheckbox, BButton, BInputGroupAppend, BFormRadioGroup, BFormRadio } from 'bootstrap-vue'
import { mapActions, mapGetters } from 'vuex'
import { AxiosResponse } from 'axios'
import { emitter } from '@kwixeo/interface-vue'
import { CustomFieldsForm } from '@kwixeo/custom-fields-vue'

@Component({
  components: {
    KForm, KFormInput, KFormSelect, KFormEditor, KFormNumber, KFormSpinner, KFormCheckbox,
    BFormRow, BCol, BFormGroup, BRow, BTabs, BTab, BInputGroup, BFormCheckbox, BButton, BInputGroupAppend, BFormRadioGroup, BFormRadio,
    CustomFieldsForm
  },
  methods: {
    ...mapActions('globalDatas', ['loadTeamUsers'])
  },
  computed: {
    ...mapGetters('globalDatas', ['teamUsersRole'])
  }
})
export default class ServiceForm extends Mixins(CatalogElementMixin) {
  loadTeamUsers!: (force?: boolean) => void
  teamUsersRole!: (role: string) => KwixeoFramework.TeamUsers

  @Prop({ default: null }) value!: KwixeoCatalog.Service|null

  protected localElement: KwixeoCatalog.FormService = {
    custom_price_mode: 'quantity',
    designation: null,
    formStatus: true,
    unit_price_ht: 0,
    unit_price_tva: 0,
    unit_price_ttc: 0,
    default_quantity: 1,
    cant_change_price: false,
    allowed_all_technicians: true,
    products_stock_management: true,
    duration_type: 'hour',
    products: [],
    technicians: [],
    custom_fields: []
  }

  private durationTypes = [
    { id: 'hour', label: 'Heure' },
    { id: 'minute', label: 'Minute' }
  ]

  private productToAdd: KwixeoCatalog.Product|null = null

  async created (): Promise<void> {
    if (this.id) {
      const service = await fetchService(this.id)
      this.setLocalElement(service)
      this.loading = false
    }
  }

  async mounted (): Promise<void> {
    await this.loadTeamUsers()
  }

  // Return url for form
  get formAction (): string {
    let url = '/catalog/service'
    if (this.localElement.id) {
      url += '/' + this.localElement.id
    }
    return url
  }

  // Return url for products list
  get productApiUrl (): string {
    return '/catalog/product?sort=name'
  }

  // Return url for technical list
  get technicalApiUrl (): string {
    return '/team/user?sort=fullname&filter[roles]=technical'
  }

  // Calcultate the total workforce costs
  get totalWorkforceCosts (): number {
    const quantity = this.number(this.localElement.number_people_needed)
    const unitCosts = this.number(this.localElement.hourly_person_cost ?? this.defaultWorkforceCosts)
    const duration = this.number(this.localElement.duration)
    const hourDuration = this.number(this.localElement.duration_type === 'hour' ? duration : (duration / 60))
    return this.number(quantity * unitCosts * hourDuration)
  }

  // Return the default workforce costs
  get defaultWorkforceCosts (): number {
    return this.number(this.teamConfigs.average_hourly_person_cost)
  }

  // Calculate the products total costs
  get totalProductsCosts (): number {
    const products = this.localElement.products ?? []
    if (products.length === 0) {
      return 0
    }

    return products
      .map(product => this.number(product.product.total_costs) * this.number(product.quantity))
      .reduce((a, b) => this.number(a + b))
  }

  // Calculate the total costs
  get totalCosts (): number {
    return this.number(this.totalWorkforceCosts + this.totalProductsCosts + this.number(this.localElement.various_costs))
  }

  protected async checkElementDatas (element: KwixeoCatalog.Service): Promise<KwixeoCatalog.Service> {
    if (element.id && needFetchService(element)) {
      return fetchService(element.id)
    }
    return element
  }

  // Add a new product reference
  private addProduct (product: KwixeoCatalog.Product|null): void {
    if (!product) {
      return
    }
    const exist = this.localElement.products.find(p => p.product?.id === product.id)
    if (exist) {
      exist.quantity++
    } else {
      this.localElement.products.push({
        product: product,
        unit_price_ht: Number(product.total_costs),
        quantity: 1
      })
    }

    this.$nextTick(() => this.productToAdd = null)
  }

  private removeProduct (index: number): void {
    this.localElement.products.splice(index, 1)
  }

  private submitComplete (response: AxiosResponse): void {
    const service: KwixeoCatalog.Service = response.data
    if (this.value && this.value.id) {
      emitter.emit('serviceUpdated', { old: this.value, new: service })
    } else {
      emitter.emit('serviceCreated', service)
    }
  }
}
